/* eslint-disable */

/**
 * This function returns `a` if `b` is deeply equal.
 * If not, it will replace any deeply equal children of `b` with those of `a`.
 * This can be used for structural sharing between JSON values for example.
 */
const hasOwn = Object.prototype.hasOwnProperty;

export function replaceEqualDeep<T>(a: unknown, b: T, depth?: number): T;

// copied from here: https://github.com/TanStack/query/blob/3daac67da5f23aacac7ed481ae6de0ef9f5af5d5/packages/query-core/src/utils.ts#L272
export function replaceEqualDeep(a: any, b: any, depth = 0): any {
    if (a === b) {
        return a;
    }

    if (depth > 500) return b;

    const array = isPlainArray(a) && isPlainArray(b);

    if (!array && !(isPlainObject(a) && isPlainObject(b))) return b;

    const aItems = array ? a : Object.keys(a);
    const aSize = aItems.length;
    const bItems = array ? b : Object.keys(b);
    const bSize = bItems.length;
    const copy: any = array ? new Array(bSize) : {};

    let equalItems = 0;

    for (let i = 0; i < bSize; i++) {
        const key: any = array ? i : bItems[i];
        const aItem = a[key];
        const bItem = b[key];

        if (aItem === bItem) {
            copy[key] = aItem;
            if (array ? i < aSize : hasOwn.call(a, key)) equalItems++;
            continue;
        }

        if (
            aItem === null ||
            bItem === null ||
            typeof aItem !== "object" ||
            typeof bItem !== "object"
        ) {
            copy[key] = bItem;
            continue;
        }

        const v = replaceEqualDeep(aItem, bItem, depth + 1);
        copy[key] = v;
        if (v === aItem) equalItems++;
    }

    return aSize === bSize && equalItems === aSize ? a : copy;
}

function isPlainArray(value: unknown): value is Array<unknown> {
    return Array.isArray(value) && value.length === Object.keys(value).length;
}

// Copied from: https://github.com/jonschlinkert/is-plain-object
function isPlainObject(o: any): o is Record<PropertyKey, unknown> {
    if (!hasObjectPrototype(o)) {
        return false;
    }

    // If has no constructor
    const ctor = o.constructor;
    if (ctor === undefined) {
        return true;
    }

    // If has modified prototype
    const prot = ctor.prototype;
    if (!hasObjectPrototype(prot)) {
        return false;
    }

    // If constructor does not have an Object-specific method
    if (!prot.hasOwnProperty("isPrototypeOf")) {
        return false;
    }

    // Handles Objects created by Object.create(<arbitrary prototype>)
    if (Object.getPrototypeOf(o) !== Object.prototype) {
        return false;
    }

    // Most likely a plain Object
    return true;
}

function hasObjectPrototype(o: any): boolean {
    return Object.prototype.toString.call(o) === "[object Object]";
}
